import { LOCATION_INITIALIZED } from "@angular/common";
import {
    HTTP_INTERCEPTORS,
    provideHttpClient,
    withInterceptorsFromDi,
} from "@angular/common/http";
import {
    Injector,
    importProvidersFrom,
    inject,
    provideAppInitializer,
    LOCALE_ID,
} from "@angular/core";
import {
    RouterModule,
    Routes,
    provideRouter,
    withEnabledBlockingInitialNavigation,
} from "@angular/router";
import { ServiceWorkerModule } from "@angular/service-worker";
import { TranslateModule, TranslateService } from "@ngx-translate/core";
import { provideTranslateHttpLoader } from "@ngx-translate/http-loader";
import { definePreset } from "@primeuix/themes";
import Aura from "@primeuix/themes/aura";
import { MessageService } from "primeng/api";
import { PrimeNG, providePrimeNG } from "primeng/config";
import { applyPrimeNgLocaleFromTranslations } from "src/app/core/helpers/app/primeng-locale.util";
import { DialogService } from "primeng/dynamicdialog";
import { HttpRequestsInterceptor } from "src/app/core/interceptors/http.interceptor";
import { NgxEchartsModule } from "ngx-echarts";
import { environment } from "src/environments/environment";
import { isAuthenticated } from "src/app/core/guards/is-authenticated-and-admin.guard";

const MaritimeAura = definePreset(Aura, {
    primitive: {
        blue: {
            50: "#f4fafe",
            100: "#cae6fc",
            200: "#a0d2fa",
            300: "#75bef8",
            400: "#4baaf5",
            500: "#2196f3",
            600: "#1c80cf",
            700: "#1769aa",
            800: "#125386",
            900: "#0d3c61",
        },
        green: {
            50: "#f7faf5",
            100: "#dbe8cf",
            200: "#bed6a9",
            300: "#a1c384",
            400: "#85b15e",
            500: "#689f38",
            600: "#588730",
            700: "#496f27",
            800: "#39571f",
            900: "#2a4016",
        },
        yellow: {
            50: "#fffcf5",
            100: "#fef0cd",
            200: "#fde4a5",
            300: "#fdd87d",
            400: "#fccc55",
            500: "#fbc02d",
            600: "#d5a326",
            700: "#b08620",
            800: "#8a6a19",
            900: "#644d12",
        },
        cyan: {
            50: "#f2fcfd",
            100: "#c2eff5",
            200: "#91e2ed",
            300: "#61d5e4",
            400: "#30c9dc",
            500: "#00bcd4",
            600: "#00a0b4",
            700: "#008494",
            800: "#006775",
            900: "#004b55",
        },
        pink: {
            50: "#fef4f7",
            100: "#fac9da",
            200: "#f69ebc",
            300: "#f1749e",
            400: "#ed4981",
            500: "#e91e63",
            600: "#c61a54",
            700: "#a31545",
            800: "#801136",
            900: "#5d0c28",
        },
        indigo: {
            50: "#f6f7fc",
            100: "#d5d9ef",
            200: "#b3bae2",
            300: "#919cd5",
            400: "#707dc8",
            500: "#4e5fbb",
            600: "#42519f",
            700: "#374383",
            800: "#2b3467",
            900: "#1f264b",
        },
        teal: {
            50: "#f2faf9",
            100: "#c2e6e2",
            200: "#91d2cc",
            300: "#61beb5",
            400: "#30aa9f",
            500: "#009688",
            600: "#008074",
            700: "#00695f",
            800: "#00534b",
            900: "#003c36",
        },
        orange: {
            50: "#fffaf2",
            100: "#ffe6c2",
            200: "#ffd391",
            300: "#ffbf61",
            400: "#ffac30",
            500: "#ff9800",
            600: "#d98100",
            700: "#b36a00",
            800: "#8c5400",
            900: "#663d00",
        },
        purple: {
            50: "#faf4fb",
            100: "#e7cbec",
            200: "#d4a2dd",
            300: "#c279ce",
            400: "#af50bf",
            500: "#9c27b0",
            600: "#852196",
            700: "#6d1b7b",
            800: "#561561",
            900: "#3e1046",
        },
        red: {
            50: "#fef6f5",
            100: "#fcd2cf",
            200: "#faaea9",
            300: "#f88a82",
            400: "#f6675c",
            500: "#f44336",
            600: "#cf392e",
            700: "#ab2f26",
            800: "#86251e",
            900: "#621b16",
        },
        gray: {
            50: "#fafafa",
            100: "#f5f5f5",
            200: "#eeeeee",
            300: "#e0e0e0",
            400: "#bdbdbd",
            500: "#9e9e9e",
            600: "#757575",
            700: "#616161",
            800: "#424242",
            900: "#212121",
        },
    },
    semantic: {
        primary: {
            50: "#e6f6fc",
            100: "#b0e5f5",
            200: "#8ad8ef",
            300: "#54c7e8",
            400: "#33bce3",
            500: "#0091d2",
            600: "#0084bf",
            700: "#006795",
            800: "#005074",
            900: "#003d59",
        },
        focusRing: {
            width: "0",
            style: "none",
            color: "transparent",
            offset: "0",
        },
        colorScheme: {
            light: {
                surface: {
                    0: "#ffffff",
                    50: "#fafafa",
                    100: "#f5f5f5",
                    200: "#eeeeee",
                    300: "#e0e0e0",
                    400: "#bdbdbd",
                    500: "#9e9e9e",
                    600: "#757575",
                    700: "#616161",
                    800: "#424242",
                    900: "#212121",
                },
                primary: {
                    color: "{primary.500}",
                    contrastColor: "#ffffff",
                    hoverColor: "{primary.600}",
                    activeColor: "{primary.700}",
                },
                highlight: {
                    background: "rgba(63, 81, 181, 0.12)",
                    focusBackground: "rgba(63, 81, 181, 0.24)",
                    color: "#0091d2",
                    focusColor: "#0091d2",
                },
                mask: {
                    background: "rgba(0, 0, 0, 0.32)",
                },
                text: {
                    color: "rgba(0, 0, 0, 0.87)",
                    hoverColor: "rgba(0, 0, 0, 0.87)",
                    mutedColor: "rgba(0, 0, 0, 0.6)",
                    hoverMutedColor: "rgba(0, 0, 0, 0.74)",
                },
                content: {
                    borderColor: "{surface.200}",
                    background: "var(--p-surface-card)",
                    hoverBackground: "{surface.100}",
                    color: "{text.color}",
                },
            },
            dark: {
                surface: {
                    0: "#07161E",
                    50: "#16262F",
                    100: "#253740",
                    200: "#344751",
                    300: "#435862",
                    400: "#526873",
                    500: "#617984",
                    600: "#6F8995",
                    700: "#7E99A6",
                    800: "#8DAAB7",
                    900: "#9CBAC8",
                    950: "#07161E",
                },
                primary: {
                    color: "#0b83fc",
                    contrastColor: "#121212",
                    hoverColor: "#7986cb",
                    activeColor: "{primary.200}",
                },
                highlight: {
                    background: "rgba(159, 168, 218, 0.16)",
                    focusBackground: "rgba(159, 168, 218, 0.24)",
                    color: "#0091d2",
                    focusColor: "#0091d2",
                },
                mask: {
                    background: "rgba(0, 0, 0, 0.32)",
                },
                text: {
                    color: "rgba(255, 255, 255, 0.95)",
                    hoverColor: "rgba(255, 255, 255, 0.95)",
                    mutedColor: "rgba(255, 255, 255, 0.6)",
                    hoverMutedColor: "rgba(255, 255, 255, 0.74)",
                },
                content: {
                    borderColor: "{surface.200}",
                    background: "var(--p-surface-card)",
                    hoverBackground: "{surface.100}",
                    color: "{text.color}",
                },
            },
        },
    },
    components: {
        listbox: {
            root: {
                background: "var(--p-surface-ground)",
                color: "{content.color}",
                borderColor: "{content.border.color}",
                disabledBackground: "{highlight.background}",
            },
            option: {
                focusBackground: "{highlight.focusBackground}",
            },
        },
        inputtext: {
            root: {
                background: "var(--p-surface-ground)",
                color: "{content.color}",
                borderColor: "{content.border.color}",
                disabledBackground: "{highlight.background}",
            },
        },
        select: {
            root: {
                background: "var(--p-surface-ground)",
                color: "{content.color}",
                borderColor: "{content.border.color}",
                disabledBackground: "{highlight.background}",
            },
            option: {
                focusBackground: "{highlight.focusBackground}",
            },
            overlay: {
                background: "var(--p-surface-card)",
                borderColor: "{content.border.color}",
            },
        },
        multiselect: {
            root: {
                background: "var(--p-surface-ground)",
                color: "{content.color}",
                borderColor: "{content.border.color}",
                disabledBackground: "{highlight.background}",
            },
            option: {
                focusBackground: "{highlight.focusBackground}",
            },
            overlay: {
                background: "var(--p-surface-card)",
                borderColor: "{content.border.color}",
            },
        },
        tree: {
            node: {
                selectedBackground: "{highlight.focusBackground}",
                selectedColor: "{highlight.color}",
            },
        },
        treeselect: {
            root: {
                background: "var(--p-surface-ground)",
                color: "{content.color}",
                borderColor: "{content.border.color}",
                disabledBackground: "{highlight.background}",
                paddingY: "1rem",
            },
        },
        tabs: {
            tab: {
                borderWidth: "0",
            },
            tablist: {
                background: "var(--p-surface-card)",
            },
            tabpanel: {
                background: "var(--p-surface-card)",
                padding: "0",
            },
        },
        datatable: {
            headerCell: {
                borderColor: "{content.border.color}",
            },
            bodyCell: {
                borderColor: "{content.border.color}",
            },
            filter: {
                overlaySelect: {
                    background: "var(--p-surface-card)",
                    borderColor: "{content.border.color}",
                },
            },
        },
        treetable: {
            headerCell: {
                borderColor: "{content.border.color}",
            },
            bodyCell: {
                borderColor: "{content.border.color}",
            },
        },
        drawer: {
            root: {
                background: "var(--p-surface-card)",
                borderColor: "{content.border.color}",
            },
        },
        panelmenu: {
            item: {
                padding: "0.75rem 0.5rem",
                focusBackground: "transparent",
            },
        },
        dialog: {
            root: {
                color: "{content.color}",
                background: "{content.background}",
                borderColor: "{content.border.color}",
                borderRadius: "{content.border.radius}",
            },
        },
        toolbar: {
            root: {
                color: "{content.color}",
                background: "{content.background}",
                borderColor: "{content.border.color}",
                borderRadius: "{content.border.radius}",
            },
        },
        button: {
            colorScheme: {
                light: {
                    text: {
                        secondary: {
                            hoverBackground: "var(--p-highlight-background)",
                        },
                    },
                },
                dark: {
                    text: {
                        secondary: {
                            hoverBackground: "var(--p-highlight-background)",
                        },
                    },
                },
            },
        },
        popover: {
            content: {
                padding: "0",
            },
        },
        badge: {
            root: {
                padding: "0.8rem 0.5rem",
            },
        },
        checkbox: {
            root: {
                checkedBorderColor: "{content.border.color}",
                checkedHoverBorderColor: "{primary.color}",
            },
            icon: {
                checkedColor: "{content.color}",
                checkedHoverColor: "{primary.color}",
            },
        },
        togglebutton: {
            colorScheme: {
                light: {
                    content: {
                        checkedBackground: "{primary.color}",
                    },
                },
                dark: {
                    content: {
                        checkedBackground: "{primary.color}",
                    },
                },
            },
        },
        autocomplete: {
            overlay: {
                background: "var(--p-surface-card)",
                borderColor: "{content.border.color}",
            },
            option: {
                focusBackground: "{highlight.focusBackground}",
            },
        },
        datepicker: {
            colorScheme: {
                light: {
                    dropdown: {
                        background: "{primary.color}",
                        color: "{content.color}",
                    },
                },
                dark: {
                    dropdown: {
                        background: "{primary.color}",
                        color: "{content.color}",
                    },
                },
            },
        },
        colorpicker: {
            colorScheme: {
                light: {
                    panel: {
                        background: "var(--p-surface-card)",
                        borderColor: "{content.border.color}",
                    },
                },
                dark: {
                    panel: {
                        background: "var(--p-surface-card)",
                        borderColor: "{content.border.color}",
                    },
                },
            },
        },
        chip: {
            colorScheme: {
                light: {
                    root: {
                        color: "{content.color}",
                        background: "var(--p-surface-ground)",
                    },
                },
                dark: {
                    root: {
                        color: "{content.color}",
                        background: "var(--p-surface-ground)",
                    },
                },
            },
        },
        menu: {
            item: {
                focusBackground: "{highlight.focusBackground}",
            },
        },
    },
});

// TODO: remove after verifying theme tokens propagate
//console.debug("[MaritimeAura] surface.0 =", (MaritimeAura as any)?.semantic?.colorScheme?.light?.surface?.[0]);

function initializeTranslations(): Promise<void> {
    const translate = inject(TranslateService);
    const primeNG = inject(PrimeNG);
    const injector = inject(Injector);

    const applyPrimeNgLocale = () =>
        applyPrimeNgLocaleFromTranslations(translate, primeNG);

    translate.onLangChange.subscribe(() => applyPrimeNgLocale());

    return new Promise<void>((resolve) => {
        const locationInitialized = injector.get(
            LOCATION_INITIALIZED,
            Promise.resolve(null),
        );
        locationInitialized.then(() => {
            const supportedLangs = ["en", "el"];
            const langToSet = localStorage.getItem("preferredLang") || "en";

            translate.addLangs(supportedLangs);
            translate.setFallbackLang("en");
            translate.use(langToSet).subscribe({
                next: () => {
                    console.info(`Initialized '${langToSet}' language.'`);
                    applyPrimeNgLocale();
                },
                error: () => {
                    console.error(
                        `Problem with '${langToSet}' language initialization.'`,
                    );
                },
                complete: () => resolve(),
            });
        });
    });
}

const routes: Routes = [
    {
        path: "auth",
        loadChildren: () =>
            import("./auth/auth.routes").then((m) => m.AuthRoutes),
    },
    {
        path: "pages",
        loadChildren: () =>
            import("./pages/pages.routes").then((m) => m.PagesRoutes),
        canActivate: [isAuthenticated],
    },
    {
        path: "misc",
        loadChildren: () =>
            import("./misc/misc.routes").then((m) => m.MiscRoutes),
    },
    {
        path: "",
        redirectTo: "pages/coverage",
        pathMatch: "full",
    },
    {
        path: "**",
        redirectTo: "pages",
    },
];

export const appConfig = {
    providers: [
        providePrimeNG({
            theme: {
                preset: MaritimeAura,
                options: {
                    darkModeSelector: "[data-theme=dark]",
                    cssLayer: {
                        name: "primeng",
                        order: "theme, base, primeng",
                    },
                },
            },
        }),
        provideRouter(routes, withEnabledBlockingInitialNavigation()),
        provideHttpClient(withInterceptorsFromDi()),
        importProvidersFrom(
            RouterModule.forRoot(routes),
            NgxEchartsModule.forRoot({
                echarts: () => import("echarts"),
            }),
            TranslateModule.forRoot(),
            ServiceWorkerModule.register("/ngsw-worker.js", {
                enabled: environment.production,
            }),
        ),
        // Must come after TranslateModule.forRoot() so TranslateLoader is not overridden by TranslateNoOpLoader.
        ...provideTranslateHttpLoader({
            prefix: "/assets/i18n/",
            suffix: ".json",
            useHttpBackend: true,
        }),
        {
            provide: LOCALE_ID,
            useFactory: () => localStorage.getItem("preferredLang") || "en",
        },
        {
            provide: HTTP_INTERCEPTORS,
            useClass: HttpRequestsInterceptor,
            multi: true,
        },
        provideAppInitializer(initializeTranslations),
        MessageService,
        DialogService,
    ],
};
